<template>
	<div class="flex flex-col flex-1 pb-2 overflow-x-auto">
		<div class="flex gap-2 items-center justify-between mt-2">
			<div class="flex flex-1">
				<slot name="searchFilter">
					<app-input
						v-if="searchable"
						v-model="computedSearch"
						name="search"
						:placeholder="$t('search')"
						class="flex-1"
						:icon="searching ? 'spin-loading' : 'search'"
						@update:modelValue="_onSearchInput"
					/>
				</slot>
			</div>
			<app-button icon="download" class="btn btn-primary transparent" @click="exportTable" :loading="exporting" />
		</div>
		<table class="app-table" :class="$attrs.class ? $attrs.class : ''" v-bind="$attrs">
			<thead>
				<tr class="app-table-head-row">
					<th v-for="(column, index) in columns" :key="index" class="flex-1">
						<div class="flex flex-wrap flex-1">
							<p :class="['flex flex-1', column.alignment === 'RIGHT' ? 'justify-end' : 'justify-start']">
								{{ formatDynamicColumnName(column.name) }}
							</p>
							<p class="flex flex-col pl-1">
								<app-icon
									:class="
										sortByDirection === 'asc' && sortBy === column.name
											? 'text-primary'
											: 'text-secondary'
									"
									class="float-right self-center h-8px cursor-pointer"
									icon="sort-up"
									@click="fireSortBy(column.name, 'asc')"
								/>
								<app-icon
									:class="
										sortByDirection !== 'asc' && sortBy === column.name
											? 'text-primary'
											: 'text-secondary'
									"
									class="float-right self-center h-8px cursor-pointer"
									icon="sort-down"
									@click="fireSortBy(column.name, 'desc')"
								/>
							</p>
						</div>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(tr, trIndex) in tableData" :key="trIndex" class="app-table-body-row">
					<td v-for="(td, tdIndex) in tr" :key="tdIndex">
						<span
							:class="[
								'flex',
								columns[tdIndex].type === 'class java.lang.String' ||
								columns[tdIndex].type === 'class java.lang.Timestamp'
									? 'justify-start'
									: 'justify-end',
								td.bold ? 'font-bold' : '',
							]"
						>
							<app-details-shortcut
								v-if="td.referenceTypeId"
								:routeName="getRouteName(td.referenceTypeId)"
								:identifier="getIdentifier(td)"
							>
								<span v-if="td.referenceTypeId == 1" class="text-info"
									>#{{ formatOrderId(td.referenceId) }}</span
								>
							</app-details-shortcut>
							<span v-else>{{ td.value }}</span
							><span v-if="td.hasPercentage">%</span>
							<app-icon v-if="td.hasPercentage && td.value > 0" icon="up" class="text-success" />
							<app-icon v-if="td.hasPercentage && td.value < 0" icon="down" class="text-primary" />
						</span>
					</td>
				</tr>
			</tbody>
			<tfoot v-if="tableData.length && footer.length > 0">
				<tr>
					<td
						v-for="(footerCell, footerIndex) in footer"
						:key="footerIndex"
						:style="{
							backgroundColor: footerCell.bgColor,
							color: footerCell.textColor,
						}"
					>
						<span
							:class="[
								'flex mx-1',
								(columns[footerIndex] && columns[footerIndex].type === 'class java.lang.String') ||
								(columns[footerIndex] && columns[footerIndex].type === 'class java.lang.Timestamp')
									? 'justify-start'
									: 'justify-end',
								footerCell.bold ? 'font-bold' : '',
							]"
						>
							{{ footerCell.value }}
						</span>
					</td>
				</tr>
			</tfoot>
		</table>
	</div>
</template>

<script>
	import { debounce } from 'debounce';
	import AppIcon from '@/components/app/AppIcon.vue';
	import AppDetailsShortcut from '@/components/app/AppDetailsShortcut.vue';
	import RouteNames from '@/router/names';
	import AppButton from '@/components/app/AppButton.vue';
	import ReportService from '@/services/ReportService';
	import UtilityService, { formatDynamicColumnName } from '@/services/UtilityService';
	import OrderService from '@/services/OrderService';

	export default {
		name: 'DynamicAppTable',
		components: { AppButton, AppDetailsShortcut, AppIcon },
		emits: ['rowClick', 'sortBy', 'onSearch', 'selectAllRows', 'hiddenListValues'],
		props: {
			columns: {
				type: Array,
				required: true,
			},
			rows: {
				type: Array,
				required: true,
				default: () => [],
			},
			footer: {
				type: Array,
				required: false,
				default: () => [],
			},
			loading: {
				type: Boolean,
				required: false,
			},
			searchable: {
				required: false,
				default: false,
			},
			reportName: {
				required: false,
				default: '',
			},
			remoteSearchable: {
				required: false,
				default: false,
			},
			searching: {
				required: false,
				default: false,
			},
		},
		computed: {
			computedSearch: {
				get() {
					return this.search;
				},
				set: debounce(function(newValue) {
					this.search = newValue;
				}, 100),
			},
			tableData() {
				let filteredData = [...this.rows];
				if (this.search && !this.remoteSearchable) {
					filteredData = filteredData.filter((row) => {
						return row.some((cell) => {
							const cellValue = String(cell.value).toLowerCase();
							const searchTerm = this.computedSearch.toLowerCase();
							return cellValue.includes(searchTerm);
						});
					});
				}
				if (this.sortBy) {
					const columnIndex = this.columns.findIndex((c) => c.name === this.sortBy);
					filteredData.sort((a, b) => {
						const aValue = a[columnIndex].value;
						const bValue = b[columnIndex].value;
						if (this.sortByDirection === 'asc') {
							return aValue > bValue ? 1 : -1;
						} else {
							return aValue < bValue ? 1 : -1;
						}
					});
				}
				return filteredData;
			},
		},
		data: () => ({
			RouteNames,
			sortBy: '',
			sortByDirection: '',
			search: '',
			shouldShowDetailsRow: {},
			openedIndex: -1,
			selectAll: false,
			hiddenList: false,
			exporting: false,
		}),
		methods: {
			formatDynamicColumnName,
			_onSearchInput: debounce(function(v) {
				this.$emit('onSearch', v);
			}, 300),
			fireSortBy(prop, direction) {
				this.sortBy = prop;
				this.sortByDirection = direction;
				this.$emit('sortBy', { prop, direction });
			},
			formatOrderId(id) {
				return OrderService.formatOrderId(id);
			},
			getIdentifier(td) {
				return td.referenceTypeId == 1 ? UtilityService.encrypt(td.referenceId) : td.referenceId;
			},
			getRouteName(referenceTypeId) {
				switch (referenceTypeId) {
					case 1:
						return RouteNames.OrderDetails;
					case 2:
						return RouteNames.RestaurantDetails;
					case 3:
						return RouteNames.CustomerDetails;
					case 4:
						return RouteNames.DiscountDetails;
					default:
						return '';
				}
			},
			exportTable() {
				this.exporting = true;
				ReportService.exportTable(this.columns, this.rows, this.reportName)
					.catch((e) => this.$notificationService.error(e))
					.finally(() => {
						this.exporting = false;
					});
			},
		},
	};
</script>

<style scoped>
	::-webkit-scrollbar {
		height: 5px;
	}
</style>
